import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatTimestamp } from '../../utils/dataUtils';

interface EnrichmentCardProps {
  schemaEnrichmentVersion: unknown;
  tableNames: string[];
  isMetadataMutationLoading: boolean;

  // Table enrichment
  selectedTable: string;
  setSelectedTable: (value: string) => void;
  tableDescription: string;
  setTableDescription: (value: string) => void;
  tableOwners: string;
  setTableOwners: (value: string) => void;
  tableTags: string;
  setTableTags: (value: string) => void;
  tableClassification: string;
  setTableClassification: (value: string) => void;
  tableSensitivity: string;
  setTableSensitivity: (value: string) => void;
  tableLastUpdatedAt: string | undefined;
  tableLastUpdatedBy: string | undefined;
  onSaveTableEnrichment: () => void;

  // Field enrichment
  selectedFieldTable: string;
  setSelectedFieldTable: (value: string) => void;
  selectedFieldName: string;
  setSelectedFieldName: (value: string) => void;
  selectedTableColumns: string[];
  fieldDescription: string;
  setFieldDescription: (value: string) => void;
  fieldOwners: string;
  setFieldOwners: (value: string) => void;
  fieldTags: string;
  setFieldTags: (value: string) => void;
  fieldClassification: string;
  setFieldClassification: (value: string) => void;
  fieldSensitivity: string;
  setFieldSensitivity: (value: string) => void;
  fieldLastUpdatedAt: string | undefined;
  fieldLastUpdatedBy: string | undefined;
  onSaveFieldEnrichment: () => void;

  enrichmentSaving: boolean;
}

const EnrichmentCard: React.FC<EnrichmentCardProps> = ({
  schemaEnrichmentVersion,
  tableNames,
  isMetadataMutationLoading,

  selectedTable,
  setSelectedTable,
  tableDescription,
  setTableDescription,
  tableOwners,
  setTableOwners,
  tableTags,
  setTableTags,
  tableClassification,
  setTableClassification,
  tableSensitivity,
  setTableSensitivity,
  tableLastUpdatedAt,
  tableLastUpdatedBy,
  onSaveTableEnrichment,

  selectedFieldTable,
  setSelectedFieldTable,
  selectedFieldName,
  setSelectedFieldName,
  selectedTableColumns,
  fieldDescription,
  setFieldDescription,
  fieldOwners,
  setFieldOwners,
  fieldTags,
  setFieldTags,
  fieldClassification,
  setFieldClassification,
  fieldSensitivity,
  setFieldSensitivity,
  fieldLastUpdatedAt,
  fieldLastUpdatedBy,
  onSaveFieldEnrichment,

  enrichmentSaving,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Schema Enrichment</Typography>
        {schemaEnrichmentVersion !== null && (
          <Typography variant="caption" color="text.secondary">
            Snapshot v{String(schemaEnrichmentVersion)}
          </Typography>
        )}
      </Stack>
      <Stack spacing={3}>
        {/* Table Metadata */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Table Metadata
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth disabled={!tableNames.length}>
              <InputLabel id="enrichment-table-select-label">Select Table</InputLabel>
              <Select
                labelId="enrichment-table-select-label"
                label="Select Table"
                value={selectedTable}
                onChange={(event) => setSelectedTable(event.target.value)}
              >
                {tableNames.length === 0 && (
                  <MenuItem value="" disabled>
                    No tables available
                  </MenuItem>
                )}
                {tableNames.map((name) => (
                  <MenuItem key={`enrichment-table-${name}`} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={tableDescription}
              onChange={(event) => setTableDescription(event.target.value)}
              multiline
              minRows={2}
              size="small"
            />
            <TextField
              label="Owners (comma separated)"
              value={tableOwners}
              onChange={(event) => setTableOwners(event.target.value)}
              size="small"
            />
            <TextField
              label="Tags (comma separated)"
              value={tableTags}
              onChange={(event) => setTableTags(event.target.value)}
              size="small"
            />
            <TextField
              label="Classification"
              value={tableClassification}
              onChange={(event) => setTableClassification(event.target.value)}
              size="small"
            />
            <TextField
              label="Sensitivity"
              value={tableSensitivity}
              onChange={(event) => setTableSensitivity(event.target.value)}
              size="small"
            />
            <Button
              variant="outlined"
              onClick={onSaveTableEnrichment}
              disabled={!selectedTable || enrichmentSaving || isMetadataMutationLoading}
            >
              Save Table Enrichment
            </Button>
            {tableLastUpdatedAt && (
              <Typography variant="caption" color="text.secondary">
                Snapshot last updated {formatTimestamp(tableLastUpdatedAt)} by{' '}
                {tableLastUpdatedBy ?? '\u2014'}
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Field Metadata */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Field Metadata
          </Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth disabled={!tableNames.length}>
              <InputLabel id="enrichment-field-table-select-label">Select Table</InputLabel>
              <Select
                labelId="enrichment-field-table-select-label"
                label="Select Table"
                value={selectedFieldTable}
                onChange={(event) => setSelectedFieldTable(event.target.value)}
              >
                {tableNames.length === 0 && (
                  <MenuItem value="" disabled>
                    No tables available
                  </MenuItem>
                )}
                {tableNames.map((name) => (
                  <MenuItem key={`enrichment-field-table-${name}`} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth
              disabled={!selectedFieldTable || selectedTableColumns.length === 0}
            >
              <InputLabel id="enrichment-field-select-label">Select Field</InputLabel>
              <Select
                labelId="enrichment-field-select-label"
                label="Select Field"
                value={selectedFieldName}
                onChange={(event) => setSelectedFieldName(event.target.value)}
              >
                {selectedTableColumns.length === 0 && (
                  <MenuItem value="" disabled>
                    No fields available
                  </MenuItem>
                )}
                {selectedTableColumns.map((column) => (
                  <MenuItem
                    key={`enrichment-field-${selectedFieldTable}-${column}`}
                    value={column}
                  >
                    {column}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={fieldDescription}
              onChange={(event) => setFieldDescription(event.target.value)}
              multiline
              minRows={2}
              size="small"
            />
            <TextField
              label="Owners (comma separated)"
              value={fieldOwners}
              onChange={(event) => setFieldOwners(event.target.value)}
              size="small"
            />
            <TextField
              label="Tags (comma separated)"
              value={fieldTags}
              onChange={(event) => setFieldTags(event.target.value)}
              size="small"
            />
            <TextField
              label="Classification"
              value={fieldClassification}
              onChange={(event) => setFieldClassification(event.target.value)}
              size="small"
            />
            <TextField
              label="Sensitivity"
              value={fieldSensitivity}
              onChange={(event) => setFieldSensitivity(event.target.value)}
              size="small"
            />
            <Button
              variant="outlined"
              onClick={onSaveFieldEnrichment}
              disabled={
                !selectedFieldTable ||
                !selectedFieldName ||
                selectedTableColumns.length === 0 ||
                enrichmentSaving ||
                isMetadataMutationLoading
              }
            >
              Save Field Enrichment
            </Button>
            {fieldLastUpdatedAt && (
              <Typography variant="caption" color="text.secondary">
                Snapshot last updated {formatTimestamp(fieldLastUpdatedAt)} by{' '}
                {fieldLastUpdatedBy ?? '\u2014'}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default EnrichmentCard;
